import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { History, ClipboardList, Truck, PackageCheck, Play, CheckCircle2 } from "lucide-react";
import { fmtDateTime } from "@/lib/constants";
import { stageViews } from "@/lib/stages";

const fmt = (n) => Math.round(Number(n || 0)).toLocaleString("en-IN"); // production units are whole pieces

const ACTIONS = {
  logged: { icon: ClipboardList, cls: "bg-blue-100 text-blue-700", label: (h) => `Logged ${fmt(h.qty)} pcs` },
  shipped_to_vendor: { icon: Truck, cls: "bg-orange-100 text-orange-700", label: (h) => `Sent ${fmt(h.qty)} pcs to vendor` },
  received_from_vendor: { icon: PackageCheck, cls: "bg-emerald-100 text-emerald-700", label: (h) => `Received ${fmt(h.qty)} pcs back` },
  started: { icon: Play, cls: "bg-slate-100 text-slate-600", label: () => "Stage started" },
  completed: { icon: CheckCircle2, cls: "bg-emerald-100 text-emerald-700", label: () => "Stage completed" },
};

function Entry({ h, i }) {
  const meta = ACTIONS[h.action] || { icon: History, cls: "bg-slate-100 text-slate-600", label: () => h.action };
  const Icon = meta.icon;
  return (
    <li className="relative pl-10 pb-5 last:pb-0" data-testid={`history-entry-${i}`}>
      <span className="absolute left-0 top-0 h-7 w-7 rounded-full flex items-center justify-center bg-white ring-1 ring-slate-200">
        <span className={`h-5 w-5 rounded-full flex items-center justify-center ${meta.cls}`}><Icon className="h-3 w-3" /></span>
      </span>
      <p className="text-sm font-semibold text-slate-800">{meta.label(h)} <span className="font-normal text-slate-500">· {h.stage}</span></p>
      {h.note && <p className="text-[11px] text-slate-500 font-mono-data">{h.note}</p>}
      <p className="text-[11px] text-slate-400 mt-0.5">by <span className="font-medium text-slate-600">{h.actor || "system"}</span> · {fmtDateTime(h.at)}</p>
    </li>
  );
}

export default function StageHistoryDrawer({ order, onClose }) {
  if (!order) return null;
  const history = [...(order.stage_history || [])].reverse();
  const views = stageViews(order);
  return (
    <Sheet open onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid="stage-history-drawer">
        <SheetHeader>
          <SheetTitle className="font-display flex items-center gap-2"><History className="h-4 w-4 text-indigo-600" /> Stage History · {order.order_code}</SheetTitle>
          <SheetDescription>{order.product_name} · {fmt(order.target_quantity)} pcs · {order.status}</SheetDescription>
        </SheetHeader>
        <div className="mt-4 rounded-lg border border-slate-200 p-3 space-y-1.5" data-testid="history-stage-summary">
          {views.map((v, i) => (
            <div key={`${v.name}-${i}`} className="flex items-center justify-between text-xs">
              <span className="text-slate-600 truncate">{i + 1} · {v.name}{v.isOsp ? " (OSP)" : ""}</span>
              <span className={`font-mono-data font-semibold ${v.isDone ? "text-emerald-600" : v.isActive ? (v.isOsp ? "text-orange-600" : "text-blue-600") : "text-slate-400"}`}>
                {fmt(v.done)} / {fmt(order.target_quantity)}{v.isOsp && v.atVendor > 0 ? ` · ${fmt(v.atVendor)} at vendor` : ""}
              </span>
            </div>
          ))}
        </div>
        <h4 className="mt-5 mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">{history.length} move{history.length !== 1 ? "s" : ""} · newest first</h4>
        {history.length === 0 ? (
          <p className="text-sm text-slate-400 italic" data-testid="history-empty">No stage activity yet.</p>
        ) : (
          <ol className="relative border-l border-slate-200 ml-3.5 pl-0 [&>li]:-ml-[15px]">
            {history.map((h, i) => <Entry key={`${h.at}-${i}`} h={h} i={i} />)}
          </ol>
        )}
      </SheetContent>
    </Sheet>
  );
}
